import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiFetch } from '../../api/client';
import {
    FaArrowLeft,
    FaPaperPlane,
    FaUser,
    FaHeadset,
    FaPaperclip,
    FaDownload
} from 'react-icons/fa';
import './TicketDetail.css';

interface TicketMessage {
    id: number;
    content: string;
    isStaff: boolean;
    isInternalNote: boolean;
    createdAt: string;
    staffName?: string;
}

interface TicketAttachment {
    id: number;
    filename: string;
    fileUrl: string;
    uploadedAt: string;
}

interface TicketDetail {
    id: number;
    title: string;
    description: string;
    status: string;
    priority: string;
    category: string;
    createdAt: string;
    updatedAt: string;
    messages: TicketMessage[];
    attachments: TicketAttachment[];
    billable: boolean;
    billedHours?: number;
    billedAmount?: number;
    hourlyRate?: number;
    consumedFreeQuota?: boolean;
}

export function TicketDetail() {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const [ticket, setTicket] = useState<TicketDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [newMessage, setNewMessage] = useState('');
    const [sending, setSending] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (id) loadTicket();
    }, [id]);

    useEffect(() => {
        scrollToBottom();
    }, [ticket?.messages]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const loadTicket = async () => {
        setLoading(true);
        try {
            const res = await apiFetch(`/api/support/tickets/${id}`);
            if (!res.ok) throw new Error('Errore caricamento ticket');
            const data = await res.json();
            setTicket(data);
        } catch (err: any) {
            alert('Errore: ' + err.message);
            navigate('/support');
        } finally {
            setLoading(false);
        }
    };

    const handleSendMessage = async () => {
        if (!newMessage.trim() || !id) return;

        setSending(true);
        try {
            const res = await apiFetch(`/api/support/tickets/${id}/messages`, {
                method: 'POST',
                body: JSON.stringify({ content: newMessage })
            });
            if (!res.ok) throw new Error('Errore invio messaggio');
            setNewMessage('');
            loadTicket();
        } catch (err: any) {
            alert('Errore: ' + err.message);
        } finally {
            setSending(false);
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'OPEN': return '#3498db';
            case 'IN_PROGRESS': return '#f39c12';
            case 'WAITING_CUSTOMER': return '#9b59b6';
            case 'RESOLVED': return '#2ecc71';
            case 'CLOSED': return '#95a5a6';
            default: return '#95a5a6';
        }
    };

    const getStatusLabel = (status: string) => {
        switch (status) {
            case 'OPEN': return 'Aperto';
            case 'IN_PROGRESS': return 'In Lavorazione';
            case 'WAITING_CUSTOMER': return 'In Attesa';
            case 'RESOLVED': return 'Risolto';
            case 'CLOSED': return 'Chiuso';
            default: return status;
        }
    };

    if (loading || !ticket) {
        return (
            <div className="ticket-loading">
                <div className="spinner"></div>
                <p>Caricamento ticket...</p>
            </div>
        );
    }

    return (
        <div className="ticket-detail-page">
            <div className="ticket-detail-header">
                <button className="btn-back" onClick={() => navigate('/support')}>
                    <FaArrowLeft /> Torna ai Ticket
                </button>
                <div className="ticket-info">
                    <h1>{ticket.title}</h1>
                    <div className="ticket-meta">
                        <span
                            className="badge"
                            style={{ backgroundColor: getStatusColor(ticket.status) }}
                        >
                            {getStatusLabel(ticket.status)}
                        </span>
                        <span className="ticket-id">Ticket #{ticket.id}</span>
                        <span className="ticket-date">
                            Creato il {new Date(ticket.createdAt).toLocaleDateString('it-IT')}
                        </span>
                    </div>
                    <div
                        className={`billing-banner ${ticket.consumedFreeQuota ? 'billing-banner-free' : ticket.billable ? 'billing-banner-billable' : 'billing-banner-nonbillable'}`}
                    >
                        {ticket.consumedFreeQuota
                            ? 'Questo ticket è coperto dalla quota gratuita di assistenza: non verrà fatturato.'
                            : ticket.billable
                                ? 'Ticket potenzialmente fatturabile: ogni intervento potrebbe generare costi aggiuntivi.'
                                : 'Ticket considerato gratuito: nessun addebito previsto per questa richiesta.'}
                    </div>
                </div>
            </div>

            <div className="ticket-content">
                <div className="ticket-main">
                    <div className="initial-message">
                        <div className="message-header">
                            <FaUser className="message-icon" />
                            <div>
                                <strong>La tua richiesta</strong>
                                <span className="message-time">
                                    {new Date(ticket.createdAt).toLocaleString('it-IT')}
                                </span>
                            </div>
                        </div>
                        <p className="message-content">{ticket.description}</p>
                    </div>

                    <div className="messages-container">
                        {ticket.messages && ticket.messages.length > 0 ? (
                            ticket.messages
                                .filter(msg => !msg.isInternalNote)
                                .map(message => (
                                    <div
                                        key={message.id}
                                        className={`message ${message.isStaff ? 'staff-message' : 'user-message'}`}
                                    >
                                        <div className="message-header">
                                            {message.isStaff ? (
                                                <FaHeadset className="message-icon staff" />
                                            ) : (
                                                <FaUser className="message-icon user" />
                                            )}
                                            <div>
                                                <strong>
                                                    {message.isStaff ? (message.staffName || 'Supporto QVADI') : 'Tu'}
                                                </strong>
                                                <span className="message-time">
                                                    {new Date(message.createdAt).toLocaleString('it-IT')}
                                                </span>
                                            </div>
                                        </div>
                                        <p className="message-content">{message.content}</p>
                                    </div>
                                ))
                        ) : (
                            <div className="no-messages">
                                <p>Nessuna risposta ancora. Il nostro team sta analizzando la tua richiesta.</p>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    {ticket.status !== 'CLOSED' && ticket.status !== 'RESOLVED' && (
                        <div className="message-input-container">
                            <textarea
                                className="message-input"
                                placeholder="Scrivi un messaggio... (Ctrl+Enter per inviare)"
                                value={newMessage}
                                onChange={e => setNewMessage(e.target.value)}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter' && e.ctrlKey) {
                                        handleSendMessage();
                                    }
                                }}
                                rows={3}
                            />
                            <button
                                className="btn-send"
                                onClick={handleSendMessage}
                                disabled={sending || !newMessage.trim()}
                            >
                                <FaPaperPlane /> {sending ? 'Invio...' : 'Invia'}
                            </button>
                        </div>
                    )}
                </div>

                <div className="ticket-sidebar">
                    <div className="sidebar-card">
                        <h3>Dettagli Ticket</h3>
                        <div className="detail-row">
                            <span className="detail-label">Categoria:</span>
                            <span className="detail-value">{ticket.category}</span>
                        </div>
                        <div className="detail-row">
                            <span className="detail-label">Priorità:</span>
                            <span className="detail-value">{ticket.priority}</span>
                        </div>
                        <div className="detail-row">
                            <span className="detail-label">Stato:</span>
                            <span className="detail-value">{getStatusLabel(ticket.status)}</span>
                        </div>
                        <div className="detail-row">
                            <span className="detail-label">Copertura:</span>
                            <span className="detail-value">
                                {ticket.consumedFreeQuota
                                    ? 'Quota gratuita'
                                    : ticket.billable
                                        ? 'Fatturabile'
                                        : 'Non fatturabile'}
                            </span>
                        </div>
                        {ticket.billable && ticket.billedHours !== undefined && (
                            <div className="detail-row">
                                <span className="detail-label">Ore Fatturate:</span>
                                <span className="detail-value">{ticket.billedHours}h</span>
                            </div>
                        )}
                        {ticket.billable && ticket.hourlyRate !== undefined && (
                            <div className="detail-row">
                                <span className="detail-label">Tariffa Oraria:</span>
                                <span className="detail-value">€{ticket.hourlyRate}/h</span>
                            </div>
                        )}
                        {ticket.billable && ticket.billedAmount !== undefined && (
                            <div className="detail-row">
                                <span className="detail-label">Importo Stimato:</span>
                                <span className="detail-value">€{ticket.billedAmount}</span>
                            </div>
                        )}
                    </div>

                    {ticket.attachments && ticket.attachments.length > 0 && (
                        <div className="sidebar-card">
                            <h3>Allegati</h3>
                            <div className="attachments-list">
                                {ticket.attachments.map(attachment => (
                                    <a
                                        key={attachment.id}
                                        href={attachment.fileUrl}
                                        download
                                        className="attachment-item"
                                    >
                                        <FaPaperclip />
                                        <span>{attachment.filename}</span>
                                        <FaDownload className="download-icon" />
                                    </a>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
